import { GenerateImageOptions, GenerateImageRendererOptions, MaterialFace, ModelViewerMaterialPrimitiveType } from "@centricsoftware/model-viewer";
import JsonResponse from "csi/comm/JsonResponse";
import { getNodeAttributeImageSrc } from "csi/model/image";
import { resolveNode, resolveNodes } from "csi/service/nodeService";
import { saveNewTemplate, saveTemplateChanges } from "csi/state/3D/snapshotsService";
import ThreeDStore from "csi/state/3D/threeDViewerStore";
import { C8Node, C8NodeType, cnl } from "csi/typings";
import { sortByString } from "csi/utility/arrayUtility";
import { combineHexAlpha, parseHexAlpha } from "csi/utility/colorUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { TRANSPARENT } from "designSystem/components/colorPicker/ColorPicker";
import { RadioOption } from "designSystem/components/radio/RadioGroup";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { isEqual, isEqualWith } from "lodash";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { ChangeEvent, createContext, useContext } from "react";
import { getFileVaultRequestUrl } from "src/c8-platform/core/request";
import Cache from "src/csi/state/Cache";
import SnapshotTemplateDialogStore from "./dialogs/SnapshotTemplateDialogStore";
import { DEFAULT_BACKGROUND_COLOR, GenerateImageFormat, GenerateImageType, MATERIAL_GEOMETRIES, MATERIAL_SIDES, SnapshotMimeType, SnapshotTemplateId, SnapshotTemplateOptions, TemplateOptions, cameraPositionOptionsFromTemplate, customTemplateOptionPositionSort, imageRatioMap, materialPrimitivesFromTemplate, materialSidesFromTemplate, normalizeExposure } from "./snapshotUtils";
import { LocalGenerateImageStore } from "../snapshots/GenerateImagesStore";

type ImageOptionsCallback = (options: GenerateImageOptions) => void
type RendererOptionsCallback = (options: GenerateImageRendererOptions) => void
type TemplateOptionsCallback = (options: SnapshotTemplateOptions) => void
type UserCanSaveTemplateCallback = (canSave: boolean) => void
type DataHasChangedCallback = (response: JsonResponse) => void

export class SnapshotTemplateStore extends ReactionStore {
	/** reference to new template dialog store if it is initialized */
	readonly dialogStore: SnapshotTemplateDialogStore | null;
	
	/** Force close jpg transparent error modal */
	@observable forceCloseJpgTransparentErrorModal = false;
	
	/** reference to generate images store if it is initialized */
	readonly generateImagesStore: LocalGenerateImageStore | null;
	
	/** Image Format */
	@observable imageFormat: GenerateImageFormat = GenerateImageFormat.PNG;
	
	/** Image Count */
	@observable.ref imageCount: ComponentItem = null;
	
	/** Image Ratio */
	@observable.ref imageRatio: ComponentItem = null;
	
	/** Image Type */
	@observable imageType: GenerateImageType = GenerateImageType.Image;
	
	/** Select all variants */
	@observable includeAllVariants: boolean = false;
	
	/** Flag that the template has been launched from copy action */
	@observable readonly isCopyTemplate: boolean = false;

	/** cache to store environment & Style type queries */
	localCache: Cache = new Cache();
	
	/** locale provider to be used in stores */
	readonly localeProvider: ILocaleProvider = null;
	
	/** save error */
	@observable saveErrorMessage: string | null = "";
	
	/** Selected Background Color */
	@observable selectedBackgroundColor = DEFAULT_BACKGROUND_COLOR;
	
	/** Stores the selected camera positions to generate images */
	@observable.ref selectedCameraPositions: ComponentItem[] = [];
	
	/** Selected Environment */
	@observable.ref selectedEnvironment: ComponentItem = null;
	
	/** Material primitive options */
	@observable.ref selectedMaterialPrimitives: ComponentItem[] = [];
	
	/** Material side options */
	@observable.ref selectedMaterialSides: ComponentItem[] = [];

	/** Selected product type components */
	@observable.ref selectedProductTypes: ComponentItem[] = [];
	
	/** Selected Template */
	@observable.ref selectedTemplate: ComponentItem = null;
	
	/** variant options */
	@observable.ref selectedVariants: ComponentItem[] = [];

	/** Track whether selectedProductTypes has been initialized */
	@observable private isProductTypesInitialized = false;
	
	/** Update cache data */
	readonly setDataHasChanged: DataHasChangedCallback | null;
	
	/** User can save template */
	readonly setUserCanSaveTemplate: UserCanSaveTemplateCallback | null;
	
	/** id for what type of template is being created/edited */
	readonly snapshotTemplateId: SnapshotTemplateId;
	
	/** Template Name */
	@observable templateName: string = "";
	
	/** Template Description */
	@observable templateDescription: string = "";
	
	/** style template urls */
	@observable.ref templateUrls: cnl[];
	
	/** reference to 3D store if it is initialized */
	@observable.ref readonly threeDStore: ThreeDStore | null;
	
	/** Use 3D Environment as Background */
	@observable use3DEnvironmentAsBackground: boolean = false;
	
	/** Is image type a turntable */
	get imageTypeIsTurntable(): boolean {
		return this.imageType === GenerateImageType.Turntable;
	}
	
	/** Image Format Options */
	get imageFormatOptions(): RadioOption<string>[] {
		return [
			{ name: this.localeProvider.resource("ImagePNG"), value: GenerateImageFormat.PNG },
			{ name: this.localeProvider.resource("ImageJPG"), value: GenerateImageFormat.JPEG },
		];
	}

	/** Image Count Options */
	get imageCountOptions(): ComponentItem[] {
		return [
			{ id: "15", value: 15 },
			{ id: "30", value: 30 },
			{ id: "60", value: 60 },
			{ id: "90", value: 90 },
		];
	}

	/** Image Ratio Options */
	get imageRatioOptions(): ComponentItem[] {
		return [
			{ id: "1:1", value: "1:1" },
			{ id: "4:3", value: "4:3" },
			{ id: "16:9", value: "16:9" },
		];
	}

	/** Image Type Options */
	get imageTypeOptions(): RadioOption<string>[] {
		return [
			{ name: this.localeProvider.resource("Image"), value: GenerateImageType.Image },
			{ name: this.localeProvider.resource("3DTurntable"), value: GenerateImageType.Turntable },
		];
	}

	/** Get rgb and alpha value from selectedbackground */
	get selectedBackgroundColorData(): { rgb: string; alpha: number } {
		return this.selectedBackgroundColor === TRANSPARENT ? { rgb: "#ffffff", alpha: 0 } : parseHexAlpha(this.selectedBackgroundColor);
	}

	/** Determine if the user has selected a jpg image with transparency */
	get jpgHasTransparentColor(): boolean {
		const { selectedBackgroundColorData, imageFormat } = this;
		const { alpha } = selectedBackgroundColorData;

		return imageFormat === GenerateImageFormat.JPEG && alpha < 1;
	}

	/** determine whether or not to show the showJpgHasTransparentColorError */
	get showJpgHasTransparentColorError(): boolean {
		return this.jpgHasTransparentColor && !this.forceCloseJpgTransparentErrorModal;
	}

	/** Get the product type localized label */
	get productTypeLocalizedLabel(): string {
		const { localeProvider, snapshotTemplateId } = this;
		const isStyle = snapshotTemplateId === SnapshotTemplateId.Style;
		return localeProvider.attributeResource(
			isStyle ? "StyleSnapshotTemplate" : "MaterialSnapshotTemplate", 
			isStyle ? "StyleTypes" : "MaterialTypes",
		);
	}

	/** Get the product type error message */
	get productTypeLocalizedErrorMessage(): string {
		const { localeProvider, requiredProductType, snapshotTemplateId } = this;
		const isStyle = snapshotTemplateId === SnapshotTemplateId.Style;
		const type = localeProvider.attributeResource(
			isStyle ? "StyleSnapshotTemplate" : "MaterialSnapshotTemplate", 
			isStyle ? "StyleType" : "MaterialType",
		);
		return localeProvider.resource("3DTemplateProductTypeErrorMessage", [type, requiredProductType]);
	}
	
	/** Required Product Type node if specified on the parent */
	@computed get requiredProductType(): C8NodeType | null {
		const parentProductTypeUrl = this.threeDStore?.productType;
		if (!parentProductTypeUrl || !this.dialogStore) {
			return null;
		}
		const { productTypes } = this;
		const currentProductType = productTypes.find((item) => item.value === parentProductTypeUrl)?.id;
		return currentProductType;
	}

	/** Style or Material type nodes */
	@computed get productTypesNodes(): C8Node[] {
		return this.localCache.data.filter((node) => node.$Type === "StyleType" || node.$Type === "MaterialType");
	}

	/** Get environments data */
	@computed get environmentNodes(): C8Node[] {
		return this.localCache.data.filter((node) => node.$Type === "ThreeDEnvironment");
	}

	/** Get the default environment url */
	@computed get defaultEnvironmentUrl(): cnl {
		const defaultEnvironment = this.environmentNodes.find((item) => item.Default)?.$URL;
		return defaultEnvironment;
	}

	/** Get the default Environment component item */
	@computed get defaultEnvironmentItem(): ComponentItem {
		return this.environments.find((item) => item.id === this.defaultEnvironmentUrl);
	}

	/** Get user defined camera positions */
	@computed get cameraPositionNodes(): C8Node[] {
		return this.localCache.data.filter((node) => node.$Type === "CameraPosition");
	}

	/** Get snapshot template data */
	@computed get templateNodes(): C8Node[] {
		return this.localCache.getNodes(this.templateUrls);
	}

	/** Node of the selected Template */
	@computed get selectedTemplateNode(): C8Node {
		return this.localCache.get(this.selectedTemplate?.id);
	}

	/** Product Type Options */
	@computed get productTypes(): ComponentItem[] {
		const { productTypesNodes } = this;
		return productTypesNodes.map((styleType) => ({
			value: styleType.$URL,
			id: styleType.$Name,
		}));
	}

	/** Avaliable environments */
	@computed get environments(): ComponentItem[]{
		const { environmentNodes } = this;
		return environmentNodes.map((item) => ({
			id: item.$URL,
			name: item.$Name,
			imageSrc: getNodeAttributeImageSrc(item, "EnvironmentImage"),
			name2: item.Default ? this.localeProvider.resource("Default") : "",
			invalid: !item.Active,
			disabled: !item.Active,
		}));
	}

	/** Avaliable snapshot templates */
	@computed get snapshotTemplateOptions(): ComponentItem[] {
		const { templateNodes } = this;
		return templateNodes.map((item) => ({
			id: item.$URL,
			node: item,
		}));
	}

	/** User defined camera positions && default camera positions that currently come from snapshot utils */
	@computed get cameraPositionOptions(): ComponentItem[] {
		const { cameraPositionNodes, localeProvider } = this;
		const userDefinedCameraPositions = cameraPositionNodes.map((item) => ({
			id: item.$URL,
			node: item,
			group: localeProvider.enumResource(item.CameraPositionType),
			imageSrc: getNodeAttributeImageSrc(item, "Preview"),
			name: item.$Name,
			invalid: !item.Active,
		}));
		sortByString(userDefinedCameraPositions, "group");
		return userDefinedCameraPositions;
	}

	/** Get Templates that apply to this nodes style type or material type */
	@computed get filteredTemplateOptions(): ComponentItem[] {
		const { snapshotTemplateOptions } = this;
		const productType = this.threeDStore?.productType;

		return snapshotTemplateOptions.filter(({ node }) => {
			const { Active, MaterialTypes, StyleTypes } = node;
			if (!productType) {
				return Active;
			}
			const isStyleTypeValid = StyleTypes?.includes(productType) || StyleTypes?.length === 0;
			const isMaterialTypeValid = MaterialTypes?.includes(productType) || MaterialTypes?.length === 0;
			
			return Active && (isStyleTypeValid || isMaterialTypeValid);
		});		
	}

	/** Get settings from selected Template */
	@computed get selectedTemplateSettings(): TemplateOptions | undefined {
		const { selectedTemplateNode } = this;
		if (!selectedTemplateNode){
			return undefined;
		}
		const { BackgroundColor, ImageFormat, ImageType, ImageRatio, ImageCount, ThreeDEnvironment, CameraPositions, Use3DEnvironmentAsBackground } = selectedTemplateNode;
		const geometry = selectedTemplateNode.BaseGeometry?.map((item) => xml.enumGetText(item));
		const materialSide = selectedTemplateNode.MaterialSide?.map((item) => xml.enumGetText(item));

		return {
			BackgroundColor,
			CameraPositions,
			ImageFormat: xml.enumGetText(ImageFormat),
			ImageType: xml.enumGetText(ImageType),
			ImageRatio: xml.enumGetText(ImageRatio),
			ImageCount: xml.enumGetText(ImageCount),
			ThreeDEnvironment,
			Use3DEnvironmentAsBackground,
			BaseGeometry: geometry,
			MaterialSide: materialSide,
		};
	};

	/** Get the ephemeral template options */
	@computed get ephemeralTemplateOptions(): TemplateOptions {
		const { selectedBackgroundColor, imageFormat, imageType, imageRatio, imageCount, selectedEnvironment, use3DEnvironmentAsBackground, selectedMaterialSides, selectedMaterialPrimitives } = this;
		return {
			BackgroundColor: selectedBackgroundColor,
			CameraPositions: imageType === GenerateImageType.Image ? this.selectedCameraPositions.map((item) => item.id): [],
			ImageFormat: imageFormat,
			ImageType: imageType,
			ImageRatio: imageRatio?.id,
			ImageCount: imageType === GenerateImageType.Turntable ? imageCount?.id : "",
			ThreeDEnvironment: selectedEnvironment?.id,
			Use3DEnvironmentAsBackground: use3DEnvironmentAsBackground,
			BaseGeometry: selectedMaterialPrimitives.map((item) => item.name),
			MaterialSide: selectedMaterialSides.map((item) => item.name),
		};
	};

	/** Determine whether or not the user has modified template settings after selecting a template */
	@computed get userHasModifiedTemplateSettings(): boolean {
		const { selectedTemplateSettings, ephemeralTemplateOptions, selectedTemplate } = this;
		if (!selectedTemplate){
			return false;
		}
		return !isEqualWith(ephemeralTemplateOptions, selectedTemplateSettings, customTemplateOptionPositionSort);
	}

	/** Determine whether or not a user can reset a template */
	@computed get userCanResetTemplate(): boolean {
		const { selectedVariants, userHasModifiedTemplateSettings, selectedTemplate } = this;
		if (this.threeDStore && this.threeDStore.snapshotsStore.variantOptions && selectedTemplate ){
			const { variantOptions } = this.threeDStore.snapshotsStore;
			const variantsFromTemplate = this.selectedTemplateNode?.IncludeAllVariants ? variantOptions : [];
			const userHasModifiedVariants = !isEqual(variantsFromTemplate, selectedVariants);
			return userHasModifiedVariants || userHasModifiedTemplateSettings;
		} else {
			return userHasModifiedTemplateSettings;
		}
	}

	/** Set Force Close flag for Jpg Transparent Error Modal */
	@action setForceCloseJpgTransparentErrorModal = (value: boolean): void => {
		this.forceCloseJpgTransparentErrorModal = value;
	};

	/** set save error */
    @action setSaveErrorMessage = (error: string): void => {
    	this.saveErrorMessage = error;
    };

	/** Set Selected Background Color */
	@action setSelectedBackgroundColor = (color: string): void => {
		this.selectedBackgroundColor = color;
	};

	/** Set Selected Environment */
	@action setSelectedEnvironment = (event, value: ComponentItem): void => {
		this.selectedEnvironment = value;
	};

	/** Set Selected Template */
	@action setSelectedTemplate = (event, value: ComponentItem): void => {
		this.selectedTemplate = value;
	};

	/** Set Image Type */
	@action setImageType = (event, value: GenerateImageType): void => {
		this.imageType = value;
	};

	/** Set Image Format */
	@action setImageFormat = (event, value: GenerateImageFormat): void => {
		this.imageFormat = value;
	};

	/** Set Image Ratio */
	@action setImageRatio = (event, value: ComponentItem): void => {
		this.imageRatio = value;
	};

	/** Set Image Count */
	@action setImageCount = (event, value: ComponentItem): void => {
		this.imageCount = value;
	};

	/** Set Select All Variants */
	@action setIncludeAllVariants = (event, value: boolean): void => {
		this.includeAllVariants = value;
	};

	/** Set Selected Material Sides */
	@action setSelectedMaterialSides = (event, value: ComponentItem[]): void => {
		this.selectedMaterialSides = value;
	};

	/** Set Selected Material Primitives */
	@action setSelectedMaterialPrimitives = (event, value: ComponentItem[]): void => {
		this.selectedMaterialPrimitives = value;
	};

	/** set selected camera positions for generating images */
	@action setSelectedCameraPositions = (event: React.ChangeEvent, cameraPositions: ComponentItem[]): void => {
		this.selectedCameraPositions = cameraPositions;
	};

	/** set the use3DEnvironmentAsBackground parameter */
	@action set3DEnvironmentAsBackground = (event: ChangeEvent<HTMLInputElement>, checked: boolean): void => {
		this.use3DEnvironmentAsBackground = checked;
	};

	/** Find potentially inactive nodes from a selected template and add them to cache */
	@action findInactiveNodes = async (selectedTemplate: C8Node): Promise<void> => {
		const unresolvedNodes = [];
		const hasInactiveEnvironment = selectedTemplate?.ThreeDEnvironment && !this.environmentNodes.some((item) => item.$URL === selectedTemplate.ThreeDEnvironment);
		if (hasInactiveEnvironment) {
			unresolvedNodes.push(selectedTemplate.ThreeDEnvironment);
		}
		selectedTemplate?.CameraPositions.reduce((acc, item) => {
			if (!this.cameraPositionNodes.some((node) => node.$URL === item)) {
				acc.push(item);
			}
			return acc;
		  }, unresolvedNodes);

		  if (unresolvedNodes.length > 0) {
			await resolveNodes(unresolvedNodes, this.localCache);
		}
	};

	/** update template defined options for all parameters */
	updateTemplateDefinedOptions = async (): Promise<void> => {
		const selectedTemplate = this.localCache.get(this.selectedTemplate?.id);
		await this.findInactiveNodes(selectedTemplate);
		runInAction(() => {
			const defaults = {
				selectedBackgroundColor: DEFAULT_BACKGROUND_COLOR,
				imageType: GenerateImageType.Image,
				imageFormat: GenerateImageFormat.PNG,
				imageRatio: null,
				imageCount: null,
				selectedEnvironment: this.defaultEnvironmentItem,
				templateDescription: "",
				templateName: "",
				includeAllVariants: false,
				selectedProductTypes: [],
				selectedCameraPositions: [],
				use3DEnvironmentAsBackground: false,
				selectedMaterialPrimitives: [],
				selectedMaterialSides: [],
			};
			if (selectedTemplate) {
				Object.assign(defaults, {
					selectedBackgroundColor: selectedTemplate.BackgroundColor,
					imageType: xml.enumGetText(selectedTemplate.ImageType) as GenerateImageType,
					imageFormat: xml.enumGetText(selectedTemplate.ImageFormat) as GenerateImageFormat,
					imageRatio: this.imageRatioOptions.find((item) => item.id === xml.enumGetText(selectedTemplate.ImageRatio)),
					imageCount: this.imageCountOptions.find((item) => item.id === xml.enumGetText(selectedTemplate.ImageCount)),
					selectedEnvironment: this.environments.find((item) => item.id === selectedTemplate.ThreeDEnvironment),
					templateDescription: selectedTemplate.Description || "",
					templateName: this.isCopyTemplate ? this.localeProvider.resource("NAME_COPY", [selectedTemplate.$Name]) : selectedTemplate.$Name,
					includeAllVariants: selectedTemplate.IncludeAllVariants,
					selectedProductTypes: this.productTypes.filter((item) => selectedTemplate.StyleTypes?.includes(item.value) || selectedTemplate.MaterialTypes?.includes(item.value)),
					selectedCameraPositions: this.cameraPositionOptions.filter((item) => selectedTemplate.CameraPositions.includes(item.id)),
					use3DEnvironmentAsBackground: selectedTemplate.Use3DEnvironmentAsBackground,
					selectedMaterialPrimitives: MATERIAL_GEOMETRIES.filter((item) => selectedTemplate.BaseGeometry?.some((geometry) => item.value === geometry)),
					selectedMaterialSides: MATERIAL_SIDES.filter((item) => selectedTemplate.MaterialSide?.some((side) => item.value === side)),
				});
			}
			Object.assign(this, defaults);
			this.selectVariantsFromTemplate();
		});
	};

	/** Set the selected variant options when a template is selected or is reset */
	@action selectVariantsFromTemplate = (): void => {
		if (this.threeDStore && this.threeDStore.snapshotsStore.variantOptions){
			const { variantOptions, setSelectedVariants } = this.threeDStore.snapshotsStore;
			const variantsFromTemplate = this.selectedTemplateNode?.IncludeAllVariants ? variantOptions : [];
			this.setSelectedVariantsOptions(null, variantsFromTemplate);
			setSelectedVariants?.(null, variantsFromTemplate);
		}
	};

	/** set the template name */
	@action setTemplateName = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, value?: string): void => {
		this.templateName = event?.target?.value || value || "";
	};

	/** set the template description */
	@action setTemplateDescription = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
		this.templateDescription = event.target.value;
	};

	/** set the Selected Variant Options to be used in the image options */
	@action setSelectedVariantsOptions = (event, value: ComponentItem[]): void => {
		this.selectedVariants = value;
		this.threeDStore?.snapshotsStore?.setSelectedVariants(event, value);
	};

	/** set the selected product types */
	@action setSelectedProductTypes = (event, value: ComponentItem[]): void => {
		this.isProductTypesInitialized = true;
		this.selectedProductTypes = value;
		this.dialogStore.setProductTypesOptions(value);

		if (this.requiredProductType){
			const { productTypeLocalizedErrorMessage, requiredProductType } = this;
			if (value.length > 0 && !this.selectedProductTypes.some((item) => item.id === requiredProductType)){
				this.dialogStore.setProductTypeError(productTypeLocalizedErrorMessage);
			} else {
				this.dialogStore.setProductTypeError(null);
			}
		}
	};

	/** set image option values based off of defaults passed into the constructor */
	@action setImageOptionsValues = (imageOptions: GenerateImageOptions): void => {
		const { camera, materials } = imageOptions;
		this.selectedCameraPositions = cameraPositionOptionsFromTemplate(imageOptions, this.cameraPositionOptions);
		this.selectedVariants = materials.variants.map((item) => ({ id: item, name: item }));
		this.selectedMaterialSides = materialSidesFromTemplate(imageOptions);
		this.selectedMaterialPrimitives = materialPrimitivesFromTemplate(imageOptions);

		this.imageType = camera.turntable ? GenerateImageType.Turntable : GenerateImageType.Image;
		this.imageCount = this.imageCountOptions.find((item) => item.value === camera.turntable?.numberOfImages);
	};

	/** set renderer option values based off of defaults passed into the constructor */
	@action setRendererOptionsValues = (rendererOptions: GenerateImageRendererOptions): void => {
		const { background, backgroundIntensity, imageRatio, imageType, environment } = rendererOptions;
		const ratio = Object.entries(imageRatioMap).find(([key, value]) => value === imageRatio)?.[0];
		const use3DEnvironmentAsBackground = environment === background;
		const backgroundColorWithAlpha = use3DEnvironmentAsBackground ? DEFAULT_BACKGROUND_COLOR : combineHexAlpha(background as string, backgroundIntensity);

		this.use3DEnvironmentAsBackground = use3DEnvironmentAsBackground;
		this.selectedBackgroundColor = backgroundColorWithAlpha;
		this.imageRatio = this.imageRatioOptions.find((item) => item.id === ratio);
		this.imageFormat = imageType === SnapshotMimeType.JPEG ? GenerateImageFormat.JPEG : GenerateImageFormat.PNG;
	};

	/** set template option values based off of defaults passed into the constructor */
	@action setTemplateOptionsValues = (templateOptions: SnapshotTemplateOptions): void => {
		const { environmentURL } = templateOptions;

		this.selectedEnvironment = this.environments.find((item) => item.id === environmentURL);

		// if a template with an id is passed in, set the selected template
		if (templateOptions.selectedTemplateId) {
			const templateNode = this.localCache.get(templateOptions.selectedTemplateId);
			if (templateNode) {
				this.isProductTypesInitialized = true;
				this.templateDescription = templateNode.Description || "";
				this.templateName = this.localeProvider.resource("NAME_COPY", [templateNode.$Name]);
				this.includeAllVariants = templateNode.IncludeAllVariants;
				this.selectedProductTypes = this.productTypes.filter((item) => templateNode.StyleTypes?.includes(item.value) || templateNode.MaterialTypes?.includes(item.value));
				this.dialogStore.setProductTypesOptions(this.selectedProductTypes);
			}
		}
	};

	/** set values based off of defaults passed into the constructor */
	setDefaultTemplateValues = (imageOptions: GenerateImageOptions, rendererOptions: GenerateImageRendererOptions, templateOptions: SnapshotTemplateOptions): void => {
		this.setImageOptionsValues(imageOptions);
		this.setRendererOptionsValues(rendererOptions);
		this.setTemplateOptionsValues(templateOptions);
	};

	/** generate renderer options for generate images function */
	get generateRendererOptions(): GenerateImageRendererOptions {
		const { imageRatio, imageFormat, selectedBackgroundColorData, use3DEnvironmentAsBackground } = this;
		const { localCache: environmentsCache, generateEnvironmentUrl } = this;

		const ratio = imageRatioMap[imageRatio?.value];
		const imageType = imageFormat === GenerateImageFormat.JPEG ? "image/jpeg" : "image/png";

		const selectedEnvironment = environmentsCache.get(this.selectedEnvironment?.id);
		const environment = generateEnvironmentUrl(this.selectedEnvironment?.id);
		const toneMappingExposure = selectedEnvironment?.Exposure ? normalizeExposure(selectedEnvironment.Exposure) : 1;
		const backgroundOrientation= selectedEnvironment?.Orientation ? selectedEnvironment.Orientation : 0;
		const { rgb, alpha } = selectedBackgroundColorData;
		const background = use3DEnvironmentAsBackground ? environment : rgb;

		const rendererOptions: GenerateImageRendererOptions = {
			background,
			backgroundIntensity: alpha,
			backgroundOrientation,
			environment,
			toneMappingExposure,
			imageRatio: ratio,
			imageType,
		};

		return rendererOptions;
	};

	/** generate the image options for the generate images function */
	get generateImageOptions(): GenerateImageOptions {
		const { selectedCameraPositions, selectedVariants, selectedMaterialSides, selectedMaterialPrimitives } = this;
		const turntableConfig = this.imageTypeIsTurntable ? {
			numberOfImages: this.imageCount?.value,
		} : undefined;

		return {
			camera: {
				cameraPositions: selectedCameraPositions?.map((item) => ({ id: item.id, name: item.name, position: item.node.Location, rotation: item.node.Rotation })),
				turntable: turntableConfig,
			},
			materials: {
				variants: selectedVariants?.map((item) => item.id),
				sides: selectedMaterialSides?.map((item) => item.id as MaterialFace),
				primitives: selectedMaterialPrimitives?.map((item) => item.id as ModelViewerMaterialPrimitiveType),
			},
		};
	}

	/** generate snapshot template options */
	get generateSnapshotTemplateOptions(): SnapshotTemplateOptions {
		const { selectedProductTypes, templateDescription, templateName, includeAllVariants, selectedEnvironment } = this;
		return {
			templateName,
			templateDescription,
			includeAllVariants,
			productTypes: selectedProductTypes,
			environmentURL: selectedEnvironment?.id || null,
			selectedTemplateId: this.selectedTemplate?.id || null,
		};
	};

	/** generate environment url */
	generateEnvironmentUrl = (value: cnl): string => {
		const selectedEnvironment = this.localCache.get(value);
		return selectedEnvironment ? getFileVaultRequestUrl(selectedEnvironment.EnvironmentFile, false) : null;
	};

	/** save a template changes */
	saveTemplate = async <T extends boolean | void = boolean>(): Promise<T> => {
		const { generateImageOptions, generateRendererOptions, generateSnapshotTemplateOptions, snapshotTemplateId } = this;
		const { onSave } = this.dialogStore ?? {};
		const templateId = this.selectedTemplate?.id;

		try {
			const response = await saveTemplateChanges(templateId, generateImageOptions, generateRendererOptions, generateSnapshotTemplateOptions, snapshotTemplateId, onSave);
			this.localCache.cacheResponse(response);
			this.setUserCanSaveTemplate?.(this.userHasModifiedTemplateSettings);
			return true as T;
		} catch (e) {
			this.setSaveErrorMessage(e.message);
		}
	};

	/** save a new template */
	@action saveNewTemplate = async (): Promise<boolean> => {
	    const { generateImageOptions, generateRendererOptions, generateSnapshotTemplateOptions, snapshotTemplateId } = this;
	    const { onSave, writer } = this.dialogStore;
	    const w = writer ?? new Writer();
		const activeFlag = this.isCopyTemplate ? this.localCache.get(this.selectedTemplate?.id).Active : true;
	    await saveNewTemplate(activeFlag, snapshotTemplateId, generateImageOptions, generateRendererOptions, generateSnapshotTemplateOptions, w, onSave)
	        .then((response) => {
	            this.setDataHasChanged?.(response);
	        })
	        .catch((e) => {
	            this.setSaveErrorMessage(e.message);
	        });
	    return true;
	};

	constructor(
		id: SnapshotTemplateId,
		cache: Cache,
		locale: ILocaleProvider,
		setImageOptionsCallback: ImageOptionsCallback,
		setRendererOptionsCallback: RendererOptionsCallback,
		setTemplateOptionsCallback: TemplateOptionsCallback,
		setUserCanSaveTemplate: UserCanSaveTemplateCallback | null,
		setDataHasChanged: DataHasChangedCallback | null,
		threeDStore: ThreeDStore | null,
		generateImagesStore: LocalGenerateImageStore | null,
		dialogStore: SnapshotTemplateDialogStore | null,
		autoPopulatedRenderOptions: GenerateImageRendererOptions | null,
		autoPopulatedImageOptions: GenerateImageOptions | null,
		autoPopulatedTemplateOptions: SnapshotTemplateOptions | null,
		selectedTemplate?: cnl | null,
		copyTemplate?: boolean,
	){
		super();
		makeObservable(this);

		this.snapshotTemplateId = id;
		this.localeProvider = locale;
		this.localCache = cache;
		this.threeDStore = threeDStore;
		this.generateImagesStore = generateImagesStore;
		this.dialogStore = dialogStore;
		this.setUserCanSaveTemplate = setUserCanSaveTemplate;
		this.setDataHasChanged = setDataHasChanged;
		this.isCopyTemplate = copyTemplate;

		this.trackReaction(reaction(
			() => this.localCache.data,
			() => {
				if (autoPopulatedRenderOptions && autoPopulatedImageOptions && autoPopulatedTemplateOptions){
					this.setDefaultTemplateValues(autoPopulatedImageOptions, autoPopulatedRenderOptions, autoPopulatedTemplateOptions);
				} else if (selectedTemplate) {
					const template = this.snapshotTemplateOptions.find((item) => item.id === selectedTemplate);
					this.setSelectedTemplate(null, template);
					this.updateTemplateDefinedOptions();
				} else if (!this.selectedTemplate) {
					this.setSelectedEnvironment(null, this.defaultEnvironmentItem);
				}
				const templateType = this.snapshotTemplateId === SnapshotTemplateId.Material ? "MaterialSnapshotTemplate" : "StyleSnapshotTemplate";
				this.templateUrls = this.localCache.data.filter((node) => node.$Type === templateType).map((node) => node.$URL);
			},
		));

		this.trackReaction(reaction(
			() => this.generateImagesStore?.dataToAdd,
			(dataToAdd) => {
				if (dataToAdd) {
					const newData = dataToAdd;
					this.localCache.cacheResponse(newData);
					const newDataURL = newData.getNonResultUrls()[0];
					this.templateUrls = [newDataURL, ...this.templateUrls];
					this.selectedTemplate = { id: newDataURL, node: this.localCache.get(newDataURL) };
				}
			},
		));

		this.trackReaction(reaction(
			() => [this.selectedBackgroundColor, this.imageFormat],
			() => {
				this.setForceCloseJpgTransparentErrorModal(false);
			},
		));
		this.trackReaction(reaction(
			() => [
				this.selectedCameraPositions,
				this.selectedVariants,
				this.imageType,
				this.imageFormat,
				this.selectedEnvironment,
				this.selectedBackgroundColor,
				this.imageRatio,
				this.imageCount,
				this.selectedMaterialSides,
				this.selectedMaterialPrimitives,
				this.use3DEnvironmentAsBackground,
			],
			() => {
				setImageOptionsCallback(this.generateImageOptions);
				setRendererOptionsCallback(this.generateRendererOptions);
				setUserCanSaveTemplate?.(this.userHasModifiedTemplateSettings);
			},
		));
		this.trackReaction(reaction(
			() => [
				this.selectedProductTypes,
				this.templateName,
				this.templateDescription,
				this.includeAllVariants,
				this.selectedEnvironment,
			],
			() => {
				setTemplateOptionsCallback?.(this.generateSnapshotTemplateOptions);
			},
		));
		this.trackReaction(reaction(
			() => this.selectedTemplate,
			() => {
				this.updateTemplateDefinedOptions();
				setUserCanSaveTemplate?.(this.userHasModifiedTemplateSettings);
			},
		));
		this.trackReaction(reaction(
			() => this.use3DEnvironmentAsBackground,
			(value) => {
				if (value){
					this.setSelectedBackgroundColor(DEFAULT_BACKGROUND_COLOR);
				}
			},
		));

		// Reaction to set selectedProductTypes when productTypes first updates
		this.trackReaction(reaction(
			() => this.productTypes, // observe changes in productTypes
			(newProductTypes) => {
				if (!this.isProductTypesInitialized && newProductTypes.length > 0) {
					// Set selectedProductTypes to productTypes if not initialized yet
					this.selectedProductTypes = [...newProductTypes]; // deep copy
					this.isProductTypesInitialized = true;
					this.dialogStore?.setProductTypesOptions(this.selectedProductTypes);
			  	}
			},
		  ));
	}
}

export const SnapshotTemplateStoreContext = createContext<SnapshotTemplateStore>(undefined);
export const useSnapshotTemplateStore = (): SnapshotTemplateStore => useContext(SnapshotTemplateStoreContext);